import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { CityDialog, type CityRow } from "./CityDialog";
import { LinkPickerDialog, type LinkOption } from "./LinkPickerDialog";

/** Standalone-create wiring: when supplied, OK POSTs the state (and its buffered cities/areas) to the
 *  System Management endpoints, re-reads `optionsUrl`, and hands back the fresh option list + the new
 *  id to select — exactly like CountryDialog. Omitted ⇒ the buffered `onOk` mode CountryDialog uses. */
export interface StateStandalone {
  optionsUrl: string;
  onAdded: (options: { id: string; name: string }[], selectedId: string) => void;
}

/** A State row buffered under a Country. `oid` set ⇒ an EXISTING state being linked (re-parented on
 *  save); absent ⇒ a NEW state created under the country on save. `countryOid` re-assigns the state
 *  to a DIFFERENT existing country (empty ⇒ the new country being built). `cities` are buffered. */
export interface StateRow {
  oid?: string;
  longName: string;
  shortName: string;
  countryOid?: string;
  cities: CityRow[];
}

interface Props {
  /** The row being edited (Edit / edit-from-link), or undefined for a new state (New). */
  initial?: StateRow;
  /** Name of the country this state is being added under — the default of the Country dropdown. */
  parentCountryLabel?: string;
  /** Buffered mode (CountryDialog): hand the row back to the parent, which persists it on its own save. */
  onOk?: (row: StateRow) => void;
  /** Standalone mode (a "+" on another form, e.g. Contact ▸ State): OK persists directly + selects. */
  standalone?: StateStandalone;
  onClose: () => void;
}

/**
 * The legacy "State" popup — the second level of Country ▸ State ▸ City ▸ Area (owner request
 * 2026-10-23, images 835-846). State* + Short Name + a City child grid (New · Link · Unlink · Edit ·
 * Export to): New / Edit open the {@link CityDialog}, Link opens the shared {@link LinkPickerDialog}.
 * A pure in-memory editor: the state (with its cities and their areas) is buffered into the Country
 * and persisted on the Country dialog's final OK.
 */
export function StateDialog({ initial, parentCountryLabel, onOk, standalone, onClose }: Props) {
  const notify = useNotify();
  const [longName, setLongName] = useState(initial?.longName ?? "");
  const [shortName, setShortName] = useState(initial?.shortName ?? "");
  const [countryOid, setCountryOid] = useState(initial?.countryOid ?? "");
  const [cities, setCities] = useState<CityRow[]>(initial?.cities ?? []);
  const [sel, setSel] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  // Existing saved countries, so the State can be re-assigned (legacy Country combo on the State popup).
  const [countryOptions, setCountryOptions] = useState<LinkOption[]>([]);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get<{ data?: { id: string; longName?: string }[] }>("/api/v1/system/countries", {
          silentLoading: true,
        } as never);
        if (alive) setCountryOptions((data?.data ?? []).map((c) => ({ id: c.id, label: c.longName ?? "" })));
      } catch {
        /* axios surfaced it */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Nested City popup (New / Edit / edit-from-link). `cityIdx` is the buffer row being replaced
  // (null = add); `cityInitial` is what the popup opens with.
  const [cityOpen, setCityOpen] = useState(false);
  const [cityIdx, setCityIdx] = useState<number | null>(null);
  const [cityInitial, setCityInitial] = useState<CityRow | undefined>(undefined);

  // City Link picker candidates (existing cities not yet on a state).
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);

  const addCity = () => {
    setCityIdx(null);
    setCityInitial(undefined);
    setCityOpen(true);
  };
  const editCity = () => {
    if (sel == null) return;
    setCityIdx(sel);
    setCityInitial(cities[sel]);
    setCityOpen(true);
  };
  const unlinkCity = () => {
    if (sel == null) return;
    setCities((c) => c.filter((_, j) => j !== sel));
    setSel(null);
  };
  const onCityOk = (row: CityRow) => {
    setCities((c) => (cityIdx == null ? [...c, row] : c.map((r, j) => (j === cityIdx ? row : r))));
    setCityOpen(false);
  };

  const openLink = async () => {
    try {
      const { data } = await api.get<{ data?: { id: string; cityName?: string }[] }>("/api/v1/system/cities", {
        silentLoading: true,
      } as never);
      // Legacy's "Link" shows EVERY city not already in THIS state's grid (it re-parents a linked one
      // to this state on save), so a city under another state still appears — matching the old form.
      const have = new Set(cities.map((r) => r.oid).filter(Boolean));
      setLinkOptions((data?.data ?? []).filter((r) => !have.has(r.id)).map((r) => ({ id: r.id, label: r.cityName ?? "" })));
      setLinkOpen(true);
    } catch {
      /* axios error dialog already surfaced it */
    }
  };
  const confirmLink = (picked: LinkOption[]) => {
    setCities((c) => [...c, ...picked.map((p): CityRow => ({ oid: p.id, cityName: p.label, areas: [] }))]);
    setLinkOpen(false);
  };
  const editLinkCity = (opt: LinkOption) => {
    setLinkOpen(false);
    setCityIdx(null);
    setCityInitial({ oid: opt.id, cityName: opt.label, areas: [] });
    setCityOpen(true);
  };

  const exportCities = () => {
    const body = cities.map((r) => `"${r.cityName}"`).join("\n");
    const blob = new Blob([`City\n${body}`], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${longName.trim() || "state"}-cities.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const ok = async () => {
    setTried(true);
    if (!longName.trim()) return;
    if (!standalone) {
      onOk?.({ oid: initial?.oid, longName: longName.trim(), shortName: shortName.trim(), countryOid: countryOid || undefined, cities });
      return;
    }
    // Standalone create (Contact ▸ State "+"): persist the state (and any buffered cities/areas) directly,
    // then re-read the host field's options + select the new state — the State-level slice of CountryDialog.
    setSaving(true);
    try {
      const stRes = await api.post<{ data?: { id?: string } }>("/api/v1/system/states", {
        longName: longName.trim(), shortName: shortName.trim() || undefined, countryId: countryOid || undefined,
      });
      const stateId = stRes.data?.data?.id ?? "";
      for (const c of cities.filter((x) => x.cityName.trim())) {
        const cityPayload = { cityName: c.cityName.trim(), stateId: c.stateOid || stateId };
        const cRes = c.oid
          ? await api.put<{ data?: { id?: string } }>(`/api/v1/system/cities/${c.oid}`, cityPayload)
          : await api.post<{ data?: { id?: string } }>("/api/v1/system/cities", cityPayload);
        const cityId = c.oid ?? cRes.data?.data?.id ?? "";
        for (const ar of c.areas.filter((x) => x.areaName.trim())) {
          const areaId = ar.oid ?? (await api.post<{ data?: { id?: string } }>("/api/v1/system/areas", { areaName: ar.areaName.trim() })).data?.data?.id ?? "";
          if (cityId && areaId) await api.post(`/api/v1/system/cities/${cityId}/areas/${areaId}/link`);
        }
      }
      const { data } = await api.get<{ data?: { id: string; name: string }[] }>(standalone.optionsUrl, { silentLoading: true } as never);
      const fresh = data?.data ?? [];
      const selected =
        (stateId && fresh.find((o) => o.id.toLowerCase() === stateId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === longName.trim())?.id ||
        stateId;
      notify.success("Saved successfully.");
      standalone.onAdded(fresh, selected);
      onClose();
    } catch {
      /* axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title="State"
      icon="ti-map-pin"
      width={520}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => void ok()} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="cd-form">
        <div className="cd-field">
          <label className="cd-label" htmlFor="st-long">
            State:<span className="req">*</span>
          </label>
          <input
            id="st-long"
            className={`op-input${tried && !longName.trim() ? " required-empty" : ""}`}
            value={longName}
            onChange={(e) => setLongName(e.target.value)}
          />
        </div>
        <div className="cd-field">
          <label className="cd-label" htmlFor="st-short">
            Short Name:
          </label>
          <input id="st-short" className="op-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
        </div>
        <div className="cd-field">
          <label className="cd-label" htmlFor="st-country">
            Country:
          </label>
          <select id="st-country" className="op-input" value={countryOid} onChange={(e) => setCountryOid(e.target.value)}>
            <option value="">{parentCountryLabel || (standalone ? "— None —" : "— this country —")}</option>
            {countryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="cd-state">
          <div className="cd-state-h">City</div>
          <div className="op-childbar">
            <button type="button" className="op-cb" onClick={addCity}>
              <i className="ti ti-plus" aria-hidden="true" /> New
            </button>
            <button type="button" className="op-cb" onClick={openLink}>
              <i className="ti ti-link" aria-hidden="true" /> Link
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={unlinkCity} disabled={sel == null}>
              <i className="ti ti-unlink" aria-hidden="true" /> Unlink
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={editCity} disabled={sel == null}>
              <i className="ti ti-pencil" aria-hidden="true" /> Edit
            </button>
            <button type="button" className={`op-cb${cities.length === 0 ? " muted" : ""}`} onClick={exportCities} disabled={cities.length === 0}>
              <i className="ti ti-file-export" aria-hidden="true" /> Export to
            </button>
          </div>
          <table className="cd-state-tbl">
            <thead>
              <tr>
                <th className="cd-sel" aria-label="select" />
                <th className="cd-edit" aria-label="edit" />
                <th>City</th>
              </tr>
            </thead>
            <tbody>
              {cities.length === 0 ? (
                <tr>
                  <td colSpan={3} className="cd-empty">
                    No data to display
                  </td>
                </tr>
              ) : (
                cities.map((r, i) => (
                  <tr key={i} className={sel === i ? "cd-rowsel" : undefined} onClick={() => setSel(i)}>
                    <td className="cd-sel">
                      <input type="checkbox" checked={sel === i} onChange={() => setSel(sel === i ? null : i)} />
                    </td>
                    <td className="cd-edit">
                      <button
                        type="button"
                        className="sd-link-edit"
                        title="Edit"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCityIdx(i);
                          setCityInitial(cities[i]);
                          setCityOpen(true);
                        }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                    </td>
                    <td>{r.cityName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {cityOpen && (
        <CityDialog initial={cityInitial} parentStateLabel={longName} onOk={onCityOk} onClose={() => setCityOpen(false)} />
      )}

      {linkOpen && (
        <LinkPickerDialog
          title="Link City"
          options={linkOptions}
          columnLabel="City"
          emptyText="No cities available to link"
          onEdit={editLinkCity}
          onOk={confirmLink}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </AppDialog>
  );
}
